import type { Specimen } from "./useSpecimens";
import { PHYSICALLY_CONSUMED_SPECIMEN_USAGES } from "./frozenSpecimenUsages";

/** Reveal confirmation only after the loaded, eligible inventory validates every pick. */
export function canConfirmFrozenSpecimenSelection(
  selectedIds: readonly Specimen["id"][],
  selectableIds: ReadonlySet<Specimen["id"]>,
  isLoading: boolean,
): boolean {
  return (
    !isLoading &&
    selectedIds.length > 0 &&
    selectedIds.every((id) => selectableIds.has(id))
  );
}

/**
 * Resolve the one specimen a treatment cycle can safely link to a step.
 * Prefer the cycle snapshot written at selection time; older cycles without a
 * snapshot can use one unambiguous `reserve` event they left behind. Never
 * pick the first of several candidates, or a sample another cycle used.
 */
export function getSingleCycleSelectedSpecimen<
  T extends Pick<Specimen, "id" | "status" | "specimenUsages">,
>(
  specimens: readonly T[],
  cycleId: string,
  selectedMaterialIds?: readonly string[] | null,
  preferredSpecimenId?: string | null,
): T | null {
  const candidates =
    selectedMaterialIds == null
      ? specimens.filter((specimen) =>
          preferredSpecimenId
            ? specimen.id === preferredSpecimenId
            : (specimen.specimenUsages ?? []).some(
                (usage) =>
                  usage.cycleId === cycleId && usage.usage === "reserve",
              ),
        )
      : specimens.filter((specimen) =>
          selectedMaterialIds.includes(specimen.id),
        );

  const selected = preferredSpecimenId
    ? candidates.find((specimen) => specimen.id === preferredSpecimenId)
    : candidates.length === 1
      ? candidates[0]
      : undefined;
  if (!selected) return null;

  const usages = selected.specimenUsages ?? [];
  const cycleUsages = usages.filter((usage) => usage.cycleId === cycleId);
  const consumedInCycle = cycleUsages.some((usage) =>
    PHYSICALLY_CONSUMED_SPECIMEN_USAGES.has(usage.usage),
  );
  const releasedInCycle = cycleUsages.some(
    (usage) => usage.usage === "release",
  );
  if (releasedInCycle && !consumedInCycle) return null;
  if (
    usages.some(
      (usage) =>
        usage.cycleId !== cycleId &&
        PHYSICALLY_CONSUMED_SPECIMEN_USAGES.has(usage.usage),
    )
  ) {
    return null;
  }
  if (
    ["discarded", "donated", "in_transit"].includes(selected.status) ||
    (selected.status === "used" &&
      !cycleUsages.some((usage) => usage.usage === "use"))
  ) {
    return null;
  }

  return selected;
}

export function getSelectionSourceCycleId(
  specimens: readonly Pick<Specimen, "id" | "cycleId">[],
  selectedIds: readonly string[],
): string | null {
  const selected = specimens.find((specimen) =>
    selectedIds.includes(specimen.id),
  );
  return selected?.cycleId ?? null;
}

/**
 * Whether a row is material she has in frozen storage right now.
 *
 * The one place this is decided. It was written out twice — in
 * `useFrozenSpecimens`, which backs the CryoVault screen, and again in the
 * homepage's inventory snapshot — and the two agreed only by luck: the second
 * copy also demanded `verificationStatus === "verified"`, so a patient with
 * fifty-four self-recorded eggs saw them listed on one surface and was told her
 * vault was empty on the other. Two surfaces reading the same rows must not
 * hold two ideas of what is in the vault.
 *
 * Verification is deliberately not part of it: who recorded a freeze is a fact
 * about the record, not about whether the material exists. A surface that
 * cares can ask separately.
 *
 * A `reserve` usage (left by older cycles) is not a consumption: it does not
 * physically remove the egg.
 *
 * A physically-consumed usage (thaw, fertilize, transfer, use) DOES remove
 * the row — those events say the material is gone from the tank. Backend
 * lays down the usage event but does not mutate `specimen.status` or the
 * placement's `isActive`, so the frontend has to read the event log or the
 * count on Care will disagree with the count on the vault.
 */
export function isInFrozenStorage(
  specimen: Pick<Specimen, "status" | "storageLocations" | "specimenUsages">,
): boolean {
  if (specimen.status !== "stored") return false;
  if (
    !specimen.storageLocations.some(
      (location) => location.isActive && location.storageType === "frozen",
    )
  ) {
    return false;
  }
  const physicallyConsumed = (specimen.specimenUsages ?? []).some((usage) =>
    PHYSICALLY_CONSUMED_SPECIMEN_USAGES.has(usage.usage),
  );
  return !physicallyConsumed;
}
